import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Field, Input, Textarea, Select } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useToastStore } from "../../stores/toast";
import { useAuthStore } from "../../stores/auth";
import type { Tenant } from "@shared/types";

// Modèles ElevenLabs supportés par ConversationRelay (cf. doc Twilio). Turbo v2.5
// = meilleure prononciation française (son « que »), Flash v2.5 = le plus rapide.
// ⚠️ NE PAS proposer de modèle hors de cette liste : les autres déclenchent
// « application error » sur ce compte.
const VOICE_MODELS = [
  { value: "turbo_v2_5", label: "Turbo v2.5 — meilleure prononciation FR (recommandé)" },
  { value: "flash_v2_5", label: "Flash v2.5 — le plus rapide" },
];
const DEFAULT_MODEL = "turbo_v2_5";
const VOICE_DEFAULTS = { model: DEFAULT_MODEL, speed: 1.0, stability: 0.6, similarity: 0.85 };

type VoiceParams = { model: string; speed: number; stability: number; similarity: number };

// Statut de la connexion Google Calendar du tenant. scope "tenant" = compte
// Google propre à l'établissement ; "global" = repli sur le compte plateforme.
type GcalStatus = { configured: boolean; connected: boolean; scope: "tenant" | "global" | null };

interface VoiceOption {
  voice_id: string;
  name: string;
  labels?: Record<string, string>;
  preview_url?: string;
}

// Format stocké : « modèle-vitesse_stabilité_similarité » (ex « turbo_v2_5-1.0_0.6_0.85 »).
// Le modèle est avant le 1er tiret ; il contient des « _ » mais pas de « - ».
function parseVoiceSettings(s: string | null): VoiceParams {
  if (!s) return { ...VOICE_DEFAULTS };
  const dash = s.indexOf("-");
  const model = dash >= 0 ? s.slice(0, dash) : DEFAULT_MODEL;
  const tail = dash >= 0 ? s.slice(dash + 1) : s; // "1.0_0.6_0.85"
  const [speed, stability, similarity] = tail.split("_").map(Number);
  if (
    speed !== undefined &&
    stability !== undefined &&
    similarity !== undefined &&
    Number.isFinite(speed) &&
    Number.isFinite(stability) &&
    Number.isFinite(similarity)
  ) {
    return { model: model || DEFAULT_MODEL, speed, stability, similarity };
  }
  return { ...VOICE_DEFAULTS };
}

function composeVoiceSettings(v: VoiceParams): string {
  return `${v.model}-${v.speed}_${v.stability}_${v.similarity}`;
}

export function TenantSettings() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [voicesError, setVoicesError] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [gcal, setGcal] = useState<GcalStatus | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const push = useToastStore((s) => s.push);
  const navigate = useNavigate();

  // L'id vient de l'URL : un client peut arriver avec un lien vers un
  // établissement qui n'est pas le sien (ancien favori, sélection persistée
  // d'une session admin sur le même navigateur…). Plutôt que d'empiler des
  // 403, on le ramène à la configuration de SON établissement.
  const allowed = isAdmin || !tenantId || !user || user.tenants.some((t) => t.tenantId === tenantId);
  useEffect(() => {
    if (allowed) return;
    const own = user?.tenants[0]?.tenantId;
    navigate(own ? `/tenants/${own}/settings` : "/dashboard", { replace: true });
  }, [allowed, user, navigate]);

  useEffect(() => {
    if (!tenantId || !allowed) return;
    api<Tenant>(`/api/tenants/${tenantId}`).then(setTenant).catch(() => setTenant(null));
    api<GcalStatus>(`/api/google/oauth/status?tenantId=${encodeURIComponent(tenantId)}`)
      .then(setGcal)
      .catch(() => setGcal(null));
  }, [tenantId, allowed]);

  // Liste des voix ElevenLabs pour la langue du tenant (avec preview_url natif —
  // aucune génération TTS, donc zéro coût de perf).
  useEffect(() => {
    const lang = (tenant?.language ?? "fr").split("-")[0] ?? "fr";
    api<VoiceOption[]>(`/api/elevenlabs/voices?language=${encodeURIComponent(lang)}`)
      .then((list) => {
        setVoices(list);
        setVoicesError(false);
      })
      .catch(() => setVoicesError(true));
  }, [tenant?.language]);

  const save = async () => {
    if (!tenant || !tenantId) return;
    setSaving(true);
    try {
      const updated = await api<Tenant>(`/api/tenants/${tenantId}`, {
        method: "PATCH",
        body: JSON.stringify(tenant),
      });
      setTenant(updated);
      push("success", "Configuration enregistrée");
    } catch {
      push("danger", "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const playPreview = (url?: string) => {
    if (!url) {
      push("danger", "Pas d'aperçu disponible pour cette voix");
      return;
    }
    if (!audioRef.current) return;
    audioRef.current.src = url;
    void audioRef.current.play().catch(() => push("danger", "Lecture impossible"));
  };

  if (!tenant) return <div>Chargement…</div>;

  const vs = parseVoiceSettings(tenant.voiceSettings);
  const setVoiceParam = (key: "speed" | "stability" | "similarity", value: number) => {
    const next = { ...vs, [key]: value };
    setTenant({ ...tenant, voiceSettings: composeVoiceSettings(next) });
  };
  const setVoiceModel = (model: string) => {
    setTenant({ ...tenant, voiceSettings: composeVoiceSettings({ ...vs, model }) });
  };

  const selectedVoice = voices.find((v) => v.voice_id === tenant.voiceId);
  // Si la voix du tenant n'est pas dans la liste (custom), on l'ajoute en option.
  const voiceOptions =
    tenant.voiceId && !selectedVoice
      ? [{ voice_id: tenant.voiceId, name: `${tenant.voiceId} (custom)` }, ...voices]
      : voices;

  return (
    <div className="settings">
      <audio ref={audioRef} hidden />
      <header className="settings__bar">
        <div className="settings__bar-head">
          <h1 className="settings__bar-title">Configuration</h1>
          <span className="settings__bar-tenant">{tenant.name}</span>
        </div>
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? "Sauvegarde…" : "Enregistrer"}
        </Button>
      </header>

      <Section title="Identité" description="Nom affiché de l'établissement">
        <Field label="Nom">
          <Input value={tenant.name} onChange={(e) => setTenant({ ...tenant, name: e.target.value })} />
        </Field>
      </Section>

      <Section title="Comportement" description="Ton de l'agent et message d'accueil">
        <Field
          label="Registre de langage"
          hint="« Vous » = professionnel (défaut). « Tu » = amical. L'agent s'y tient tout l'appel."
        >
          <Select
            value={tenant.formality}
            onChange={(e) => setTenant({ ...tenant, formality: e.target.value as Tenant["formality"] })}
          >
            <option value="vous">Vouvoiement (vous)</option>
            <option value="tu">Tutoiement (tu)</option>
          </Select>
        </Field>
        <Field label="Message d'accueil" hint="Ce que l'appelant entend au décrochage.">
          <Textarea
            value={tenant.greetingMessage ?? ""}
            onChange={(e) => setTenant({ ...tenant, greetingMessage: e.target.value || null })}
          />
        </Field>
      </Section>

      <Section title="Voix" description="La voix entendue par l'appelant">
        {voicesError ? (
          <Field
            label="Voice ID ElevenLabs"
            hint="Liste des voix indisponible — saisis l'ID manuellement (bibliothèque ElevenLabs)."
          >
            <Input
              value={tenant.voiceId ?? ""}
              onChange={(e) => setTenant({ ...tenant, voiceId: e.target.value || null })}
            />
          </Field>
        ) : (
          <Field
            label="Voix de l'agent"
            hint="Le choix de la voix n'affecte pas la latence (le modèle se règle dans les paramètres avancés)."
          >
            <div className="voice-picker">
              <Select
                value={tenant.voiceId ?? ""}
                onChange={(e) => setTenant({ ...tenant, voiceId: e.target.value || null })}
              >
                <option value="">Voix par défaut</option>
                {voiceOptions.map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name}
                    {v.labels?.accent ? ` — ${v.labels.accent}` : ""}
                  </option>
                ))}
              </Select>
              <Button
                variant="ghost"
                onClick={() => playPreview(selectedVoice?.preview_url)}
                disabled={!selectedVoice?.preview_url}
              >
                ▶ Écouter
              </Button>
            </div>
          </Field>
        )}
      </Section>

      <Section
        title="Google Calendar"
        description="Réservations vérifiées et inscrites en temps réel dans l'agenda de l'établissement"
      >
        {!gcal?.configured ? (
          <p className="settings__hint">
            Intégration Google non configurée sur ce serveur (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET).
          </p>
        ) : (
          <Field
            label={
              gcal.scope === "tenant"
                ? "✓ Connecté — compte Google propre à cet établissement"
                : gcal.scope === "global"
                  ? "Connecté via le compte principal de la plateforme"
                  : "Non connecté"
            }
            hint="La connexion ouvre l'écran Google : choisis le compte dont l'agenda recevra les rendez-vous de cet établissement."
          >
            <Button
              variant={gcal.scope === "tenant" ? "ghost" : "primary"}
              onClick={() => {
                window.location.href = `/api/google/oauth/start?tenantId=${encodeURIComponent(tenantId ?? "")}`;
              }}
            >
              {gcal.scope === "tenant" ? "Reconnecter Google Calendar" : "Connecter Google Calendar"}
            </Button>
          </Field>
        )}
      </Section>

      <button
        type="button"
        className="advanced-toggle"
        onClick={() => setShowAdvanced((v) => !v)}
        aria-expanded={showAdvanced}
      >
        {showAdvanced ? "▾" : "▸"} Paramètres avancés
      </button>

      {showAdvanced ? (
        <>
          <Section title="Langue & fuseau" description="Accent et localisation">
            <div className="settings__row2">
              <Field label="Langue / accent" hint="Code BCP-47 : fr-CA, fr-FR, en-US…">
                <Input
                  value={tenant.language}
                  onChange={(e) => setTenant({ ...tenant, language: e.target.value })}
                />
              </Field>
              <Field label="Fuseau horaire" hint="ex America/Montreal, Europe/Paris">
                <Input
                  value={tenant.timezone}
                  onChange={(e) => setTenant({ ...tenant, timezone: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title="Réglages fins de la voix" description="Modèle et diction de la synthèse">
            <Field
              label="Modèle de synthèse"
              hint="Turbo v2.5 = meilleure prononciation française (son « que »). Flash v2.5 = ~150 ms plus rapide."
            >
              <Select value={vs.model} onChange={(e) => setVoiceModel(e.target.value)}>
                {VOICE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={`Vitesse — ${vs.speed}`} hint="0.7 (lent) → 1.2 (rapide). 1.0 = naturel.">
              <input
                className="range"
                type="range"
                min={0.7}
                max={1.2}
                step={0.05}
                value={vs.speed}
                onChange={(e) => setVoiceParam("speed", Number(e.target.value))}
              />
            </Field>
            <Field label={`Stabilité — ${vs.stability}`} hint="0 (expressif, variable) → 1 (posé, régulier).">
              <input
                className="range"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={vs.stability}
                onChange={(e) => setVoiceParam("stability", Number(e.target.value))}
              />
            </Field>
            <Field
              label={`Similarité — ${vs.similarity}`}
              hint="Proximité avec la voix d'origine. 0.8–0.9 recommandé."
            >
              <input
                className="range"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={vs.similarity}
                onChange={(e) => setVoiceParam("similarity", Number(e.target.value))}
              />
            </Field>
            <Button variant="ghost" onClick={() => setTenant({ ...tenant, voiceSettings: null })}>
              Réinitialiser aux valeurs par défaut
            </Button>
          </Section>

          <Section title="Enregistrement" description="Conserver l'audio des appels">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={tenant.recordingEnabled}
                onChange={(e) => setTenant({ ...tenant, recordingEnabled: e.target.checked })}
              />
              <span>Enregistrer les appels</span>
            </label>
          </Section>

          {isAdmin ? (
            <Section title="Prompt système" description="Override avancé du comportement">
              <Field
                label="System prompt custom"
                hint="Laisse vide pour utiliser le prompt par défaut du type de tenant."
              >
                <Textarea
                  rows={10}
                  value={tenant.systemPrompt ?? ""}
                  onChange={(e) => setTenant({ ...tenant, systemPrompt: e.target.value || null })}
                />
              </Field>
            </Section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// Section de config : titre + description à gauche (sticky), champs à droite.
// Layout deux colonnes qui remplit la largeur au lieu d'une colonne étroite.
function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="settings__group">
      <div className="settings__group-head">
        <h2 className="settings__group-title">{title}</h2>
        {description ? <p className="settings__group-desc">{description}</p> : null}
      </div>
      <div className="settings__group-body">{children}</div>
    </section>
  );
}
